import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { sellerAPI, messageAPI } from '@/services/api';
import {
	MapPin,
	MessageSquare,
	Shield,
	Star,
	ShoppingBag,
	Building2,
	ChevronLeft,
	Calendar,
	Truck,
	GraduationCap,
	Home,
	Award,
	CalendarDays,
	MessageCircle,
	Stethoscope
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { toast } from 'sonner';
import ReportButton from '@/components/ReportButton';
import SellerRatingBadge from '@/components/SellerRatingBadge';
import SellerRatingsSection from '@/components/SellerRatingsSection';
import { resolveUrl } from "@/utils/adUtil";

const getMediaUrl = (url) => (url ? (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`) : '');

function AdCard({ ad, type, fromState }) {
	const { t } = useTranslation();
	const img = ad.images?.[0];
	return (
		<Link
			to={`/${ resolveUrl(type) }/${ad.slug || ad.id}`}
			state={fromState ? { from: fromState } : undefined}
			className="bg-white border border-[#E6E2D6] rounded-sm overflow-hidden group hover:border-[#8B4513]
					transition-colors"
			data-testid={`seller-${type}-${ad.id}`}
		>
			<div className="aspect-[4/3] bg-[#F2F0E9] overflow-hidden relative">
				{img ? (
					<img src={getMediaUrl(img)} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105
							transition-transform duration-500" loading="lazy" />
				) : (
					<div className="w-full h-full flex items-center justify-center text-[#D4C5A9]">
						{type === 'horse' ? <Star className="w-12 h-12" /> : <ShoppingBag className="w-12 h-12" />}
					</div>
				)}
				{ad.is_featured && (
					<span	className="absolute top-3 left-3 featured-badge px-3 py-1 text-[10px] font-bold tracking-wider uppercase
					 		text-[#1A3C34] rounded-sm">
						{ t('horse.featured') }
					</span>
				)}
			</div>
			<div className="p-4">
				<h3 className="text-base font-semibold text-[#1A3C34] line-clamp-1 group-hover:text-[#8B4513]
						transition-colors font-['Playfair_Display']">{ad.title}</h3>
				<p className="text-xl font-bold text-[#8B4513] mt-1">&euro;{ad.price?.toLocaleString()}</p>
				{ad.location_name && (
					<div className="flex items-center gap-1 mt-2 text-xs text-[#666055]"><MapPin className="w-3 h-3" />
						{ad.location_name}
					</div>
				)}
			</div>
		</Link>
	);
}

export default function SellerProfilePage() {
	const { slug } = useParams();
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [seller, setSeller] = useState(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = searchParams.get('tab') || 'horses';
	const setActiveTab = (tab) => {
		const next = new URLSearchParams(searchParams);
		if (tab && tab !== 'horses') next.set('tab', tab); else next.delete('tab');
		setSearchParams(next, { replace: true });
	};
	// Build a "from" hint so detail/edit pages can render a proper back-link
	const buildFrom = (tab, label) => {
		return { path: `/u/${ seller.slug }${tab && tab !== 'horses' ? `?tab=${tab}` : ''}`, label }
	};
	const [horses, setHorses] = useState([]);
	const [products, setProducts] = useState([]);
	const [transports, setTransports] = useState([]);
	const [schools, setSchools] = useState([]);
	const [stables, setStables] = useState([]);
	const [trainers, setTrainers] = useState([]);
	const [events, setEvents] = useState([]);
	const [vets, setVets] = useState([]);
	const [loading, setLoading] = useState(true);
	const [msgOpen, setMsgOpen] = useState(false);
	const [msg, setMsg] = useState('');
	const [sending, setSending] = useState(false);

	useDocumentMeta({
		title: seller ? `${seller.company_name || seller.name} | Equi-Market` : 'Equi-Market',
		description: seller?.bio?.slice(0, 200),
		image: seller?.cover_image ? getMediaUrl(seller.cover_image) : '',
		url: seller ? `${window.location.origin}/u/${seller.slug}` : '',
	});

	useEffect(() => {
		setLoading(true);
		Promise.all([
			sellerAPI.get(slug),
			sellerAPI.horses(slug, { limit: 60 }),
			sellerAPI.products(slug, { limit: 60 }),
			sellerAPI.transports(slug, { limit: 60 }),
			sellerAPI.schools(slug, { limit: 60 }),
			sellerAPI.stables(slug, { limit: 60 }),
			sellerAPI.trainers(slug, { limit: 60 }),
			sellerAPI.events(slug, { limit: 60 }),
			sellerAPI.vets(slug, { limit: 60 }),
		])
			.then(([sellerPromise,
							 horsesPromise,
							 productsPromise,
							 transportsPromise,
							 schoolsPromise,
							 stablesPromise,
							 trainersPromise,
				       eventsPromise,
							 vetsPromise,
						 ]) => {
				setSeller(sellerPromise.data);
				setHorses(horsesPromise.data.items || []);
				setProducts(productsPromise.data.items || []);
				setTransports(transportsPromise.data.items || []);
				setSchools(schoolsPromise.data.items || []);
				setStables(stablesPromise.data.items || []);
				setTrainers(trainersPromise.data.items || []);
				setEvents(eventsPromise.data.items || []);
				setVets(vetsPromise.data.items || []);
				// Default tab: whichever has items
				if ((horsesPromise.data.total || 0) === 0 && (productsPromise.data.total || 0) > 0) switchTab('products');
			})
			.catch(() => navigate('/sellers'))
			.finally(() => setLoading(false));
	}, [slug, navigate]);

	const sendMessage = async () => {
		if (!user) return navigate('/auth');
		if (!msg.trim()) return;
		setSending(true);
		try {
			await messageAPI.send({ recipient_id: seller.id, content: msg });
			toast.success(t('horse.message_sent') || 'Message sent!');
			setMsg('');
			setMsgOpen(false);
		} catch {
			toast.error(t('common.error'));
		}
		setSending(false);
	};

	// Sync state -> URL params (replace to avoid history pollution)
	const updateParams = (next) => {
		const sp = new URLSearchParams(searchParams);
		Object.entries(next).forEach(([k, v]) => {
			if (v === null || v === undefined || v === '') sp.delete(k);
			else sp.set(k, String(v));
		});
		setSearchParams(sp, { replace: true });
	};

	const switchTab = (tab) => {
		setActiveTab(tab);
		updateParams({ tab });
	};

	if (loading) {
		return (
			<div className="pt-24 min-h-screen flex items-center justify-center">
				<div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
			</div>
		);
	}
	if (!seller) return null;

	const displayName = seller.company_name || seller.name;
	const memberSince = seller.created_at ? new Date(seller.created_at).toISOString().split('T')[0] : '';

	return (
		<div className="pt-16 md:pt-20 min-h-screen" data-testid="seller-profile-page">
			{/* Cover */}
			<div className="relative h-48 md:h-72 bg-gradient-to-br from-[#1A3C34] via-[#2A5048] to-[#8B4513] overflow-hidden">
				{seller.cover_image && (
					<img src={getMediaUrl(seller.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
				)}
				<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
				<div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-6">
					<Link to="/sellers" className="absolute top-4 left-4 sm:left-6 lg:left-8 text-white/90 hover:text-white
							text-sm flex items-center gap-1 backdrop-blur-sm bg-black/20 px-3 py-1.5 rounded-sm"
								data-testid="back-to-sellers">
						<ChevronLeft className="w-4 h-4" /> {t('seller.directory_title')}
					</Link>
				</div>
			</div>

			{/* Identity card */}
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative">
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 md:p-8 shadow-sm"
						 data-testid="seller-identity-card">
					<div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2 mb-2">
								{seller.is_verified && (
									<span className="inline-flex items-center gap-1 bg-[#1A3C34] text-white px-2 py-0.5 text-[10px]
											font-bold uppercase tracking-wider rounded-sm" data-testid="verified-badge">
                    <Shield className="w-3 h-3" /> {t('seller.verified')}
                  </span>
								)}
							</div>
							<h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display'] truncate"
									data-testid="seller-name">{displayName}</h1>
							{seller.company_name && seller.name && seller.company_name !== seller.name && (
								<p className="text-sm text-[#666055] mt-1 flex items-center gap-1"><Building2 className="w-3 h-3" />
									{seller.name}
								</p>
							)}
							<div className="flex items-center gap-4 mt-3 text-sm text-[#666055] flex-wrap">
								<SellerRatingBadge avg={seller.rating_avg || 0} count={seller.rating_count || 0} size="md" />
								{seller.location && (
									<span className="flex items-center gap-1" data-testid="seller-location"><MapPin className="w-4 h-4" />
										{seller.location}
									</span>
								)}
								{memberSince && (
									<span className="flex items-center gap-1">
										<Calendar className="w-4 h-4" />
										{t('seller.member_since')} {memberSince}
									</span>
								)}
							</div>
							{seller.bio && (
								<p className="mt-4 text-sm text-[#3a3a3a] leading-relaxed max-w-2xl whitespace-pre-line"
									 data-testid="seller-bio">
									{seller.bio}
								</p>
							)}
						</div>
						<div className="flex flex-col gap-2 md:items-end">
							{user && user.id !== seller.id && (
								<Button
									onClick={() => setMsgOpen(true)}
									className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-11 px-6 btn-equi"
									data-testid="contact-seller-btn"
								>
									<MessageSquare className="w-4 h-4 mr-2" /> {t('seller.send_message')}
								</Button>
							)}
							{(!user || user.id !== seller.id) && (
								<div className="md:self-end">
									<ReportButton targetType="user" targetId={seller.id} />
								</div>
							)}
							{!user && (
								<Button
									onClick={() => navigate('/auth')}
									className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-11 px-6 btn-equi"
									data-testid="contact-seller-btn"
								>
									<MessageSquare className="w-4 h-4 mr-2" /> {t('seller.send_message')}
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Tabs + ads */}
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="bg-[#F2F0E9] rounded-sm p-1 gap-1 mb-6 flex flex-nowrap justify-start
							md:flex-wrap md:justify-center overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-1
							scrollbar-hide"
						 data-testid="seller-tabs">
					<button
						onClick={() => switchTab('horses')}
						className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap 
								${activeTab === 'horses' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-horses"
					>
						{ activeTab === 'horses' &&
							<img src="/favicon-inverted-32x32.png" className="w-6 h-6"/>
						}
						{ activeTab !== 'horses' &&
							<img src="/favicon-32x32.png" className="w-6 h-6"/>
						}
						{t('nav.horses')} ({horses.length})
					</button>
					<button
						onClick={() => switchTab('products')}
						className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all  whitespace-nowrap
								${activeTab === 'products' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-products"
					>
						<ShoppingBag className="w-4 h-4" /> {t('nav.products')} ({products.length})
					</button>
					<button
						onClick={() => switchTab('transports')}
						className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap 
								${activeTab === 'transports' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-transports"
					>
						<Truck className="w-4 h-4" /> {t('nav.transports')} ({transports.length})
					</button>
					<button
						onClick={() => switchTab('schools')}
						className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								${activeTab === 'schools' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-schools"
					>
						<GraduationCap className="w-4 h-4" /> {t('nav.schools')} ({schools.length})
					</button>
					<button
						onClick={() => switchTab('stables')}
						className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								${activeTab === 'stables' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-stables"
					>
						<Home className="w-4 h-4" /> {t('nav.stables')} ({stables.length})
					</button>
					<button
						onClick={() => switchTab('trainers')}
						className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								${activeTab === 'trainers' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-trainers"
					>
						<Award className="w-4 h-4" /> {t('nav.trainers')} ({trainers.length})
					</button>
					<button
						onClick={() => switchTab('events')}
						className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								${activeTab === 'events' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-events"
					>
						<CalendarDays className="w-4 h-4" /> {t('nav.events')} ({events.length})
					</button>
					<button
						onClick={() => switchTab('vets')}
						className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								${activeTab === 'vets' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-vets"
					>
						<Stethoscope className="w-4 h-4" /> {t('nav.vets')} ({vets.length})
					</button>
					<button
						onClick={() => switchTab('ratings')}
						className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								${activeTab === 'ratings' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
						data-testid="seller-tab-ratings"
					>
						<MessageCircle className="w-4 h-4" /> {t('rating.tab_ratings')} ({seller.rating_count || 0})
					</button>
				</div>

				{activeTab === 'horses' && (
					horses.length === 0 ? (
						<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-horses">
							{t('seller.no_horses')}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
								 data-testid="seller-horses-grid">
							{horses.map((ad) => (
								<AdCard
									key={ad.id}
									ad={ad}
									type="horse"
									fromState={ buildFrom(activeTab, seller.company_name || seller.name) }
								/>
							))}
						</div>
					)
				)}

				{activeTab === 'products' && (
					products.length === 0 ? (
						<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-products">
							{t('seller.no_products')}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
								 data-testid="seller-products-grid">
							{products.map((ad) => (
								<AdCard
									key={ad.id}
									ad={ad}
									type="product"
									fromState={ buildFrom(activeTab, seller.company_name || seller.name) }
								/>
							))}
						</div>
					)
				)}

				{activeTab === 'transports' && (
					transports.length === 0 ? (
						<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-transports">
							{t('seller.no_transports')}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
								 data-testid="seller-transports-grid">
							{transports.map((ad) => (
								<AdCard
									key={ad.id}
									ad={ad}
									type="transport"
									fromState={ buildFrom(activeTab, seller.company_name || seller.name) }
								/>
							))}
						</div>
					)
				)}

				{activeTab === 'schools' && (
					schools.length === 0 ? (
						<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-schools">
							{t('seller.no_schools')}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
								 data-testid="seller-schools-grid">
							{schools.map((ad) => (
								<AdCard
									key={ad.id}
									ad={ad}
									type="school"
									fromState={ buildFrom(activeTab, seller.company_name || seller.name) }
								/>
							))}
						</div>
					)
				)}

				{activeTab === 'stables' && (
					stables.length === 0 ? (
						<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-stables">
							{t('seller.no_stables')}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
								 data-testid="seller-stables-grid">
							{stables.map((ad) => (
								<AdCard
									key={ad.id}
									ad={ad}
									type="stable"
									fromState={ buildFrom(activeTab, seller.company_name || seller.name) }
								/>
							))}
						</div>
					)
				)}

				{activeTab === 'trainers' && (
					trainers.length === 0 ? (
						<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-trainers">
							{t('seller.no_trainers')}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
								 data-testid="seller-trainers-grid">
							{trainers.map((ad) => (
								<AdCard
									key={ad.id}
									ad={ad}
									type="trainer"
									fromState={ buildFrom(activeTab, seller.company_name || seller.name) }
								/>
							))}
						</div>
					)
				)}
				{activeTab === 'events' && (
					events.length === 0 ? (
						<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-events">
							{t('seller.no_events')}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
								 data-testid="seller-events-grid">
							{events.map((ad) => (
								<AdCard
									key={ad.id}
									ad={ad}
									type="event"
									fromState={ buildFrom(activeTab, seller.company_name || seller.name) }
								/>
							))}
						</div>
					)
				)}
				{activeTab === 'vets' && (
					vets.length === 0 ? (
						<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-vets">
							{t('seller.no_vets')}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
								 data-testid="seller-vets-grid">
							{vets.map((ad) => (
								<AdCard
									key={ad.id}
									ad={ad}
									type="vet"
									fromState={ buildFrom(activeTab, seller.company_name || seller.name) }
								/>
							))}
						</div>
					)
				)}

				{activeTab === 'ratings' && (
					<SellerRatingsSection sellerId={seller.id} sellerName={displayName} />
				)}
			</div>

			{/* Message dialog */}
			<Dialog open={msgOpen} onOpenChange={setMsgOpen}>
				<DialogContent className="sm:max-w-md" data-testid="contact-dialog">
					<DialogHeader>
						<DialogTitle className="font-['Playfair_Display'] text-[#1A3C34]">
							{t('seller.send_message')}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-3 pt-2">
						<p className="text-xs text-[#666055]">{t('seller.message_help')}</p>
						<Textarea
							value={msg}
							onChange={(e) => setMsg(e.target.value)}
							placeholder={t('seller.message_placeholder')}
							rows={5}
							maxLength={2000}
							data-testid="contact-msg-input"
						/>
						<div className="flex justify-end gap-2">
							<Button variant="ghost" onClick={() => setMsgOpen(false)}
											className="text-[#666055]">
								{t('common.cancel')}
							</Button>
							<Button
								onClick={sendMessage}
								disabled={sending || !msg.trim()}
								className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm btn-equi"
								data-testid="contact-msg-send"
							>
								<MessageSquare className="w-4 h-4 mr-1" /> {sending ? t('common.loading') : t('seller.send_message')}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}